import * as RadixPopover from "@radix-ui/react-popover";
import { useVirtualizer } from "@tanstack/react-virtual";
import clsx from "clsx";
import { Command } from "cmdk";
import { useCallback, useRef, useState } from "react";
import React from "react";
import { Checkbox } from "../../checkbox/Checkbox";
import { Divider } from "../../divider/Divider";
import { Icon } from "../../icon/Icon";
import { useBodyScrollLock } from "../common/hooks/useBodyScrollLock";
import { useFilteredOptions } from "../common/hooks/useFilteredOption";
import * as selectStyles from "../common/Select.css";
import { Option } from "../common/SelectBase";
import { SelectHeaderActions } from "./components/SelectHeaderActions";
import { useVirtualListAutoMeasure } from "./hooks/useVirtualListAutoMeasure";
import { MultiTriggerValue } from "./MultiTriggerValue";
import { SelectFooterActions } from "./SelectFooterActions";

/**
 * Default texts
 */
const LOADING_TEXT = "Loading...";
const SEARCH_TEXT = "Search...";
const NO_RESULTS_TEXT = "No results found.";

const VIEWPORT_HEIGHT = 800;

export interface MultiSelectProps {
  /**
   *  Controlled value - array of selected option values
   */
  options: Option[];
  /**
   *  Controlled selected values
   */
  value?: string[];
  /**
   * Placeholder text when no value is selected
   */
  placeholder?: string;
  /**
   * Whether the select is disabled. Default: false
   */
  disabled?: boolean;
  /**
   * Whether the select is searchable.
   * Default: true
   */
  searchable?: boolean;
  /**
   * Position of the dropdown relative to the trigger. Default:
   * "bottom" */
  position?: "top" | "bottom" | "left" | "right";
  /**
   * Whether the select is in a loading state.
   * Default: false
   */
  loading?: boolean;

  /**
   * Handler called when selection changes
   */
  onChange: (value: string[]) => void;
}

export function MultiSelect({
  options,
  value = [],
  placeholder,
  disabled,
  loading = false,
  position = "bottom",
  searchable = true,
  onChange
}: MultiSelectProps) {
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [search, setSearch] = useState("");

  const listRef = useRef<HTMLDivElement | null>(null);

  /**
   * hooks: filtering + row size estimation
   */
  const filteredOptions = useFilteredOptions(options, search);

  const showSelectHeaderActions = filteredOptions.length > 0 && !loading;

  const allFilteredOptionsRelevant = filteredOptions.every(
    (opt) => opt.relevant === true
  );
  /**
   * virtualizer
   */
  const virtualizer = useVirtualizer({
    count: filteredOptions.length,
    overscan: 5,
    getScrollElement: () => listRef.current,
    estimateSize: () => 45,
    getItemKey: (index) => filteredOptions[index]?.value ?? index,
    enabled: true
  });

  const virtualItems = virtualizer.getVirtualItems();

  /**
   * lock page scroll while open
   */
  useBodyScrollLock(isPopoverOpen);

  useVirtualListAutoMeasure({
    isPopoverOpen,
    virtualizer,
    filteredOptions
  });

  /**
   * toggle one option (controlled)
   */

  const toggleOption = useCallback(
    (optionValue: string) => {
      const next = value?.includes(optionValue)
        ? value.filter((v) => v !== optionValue)
        : [...(value || []), optionValue];
      onChange(next);
    },
    [onChange, value]
  );

  function handleClear() {
    onChange([]);
  }

  function handleTogglePopover() {
    setIsPopoverOpen((prev) => !prev);
    setSearch("");
  }

  const viewPortStyle = {
    height:
      !loading || filteredOptions.length === 0
        ? "auto"
        : `${VIEWPORT_HEIGHT}px`,
    overflow: !loading || filteredOptions.length === 0 ? "visible" : "auto",
    position: "relative",
    width: "100%"
  } as React.CSSProperties;

  return (
    <RadixPopover.Root open={isPopoverOpen} onOpenChange={setIsPopoverOpen}>
      <RadixPopover.Trigger asChild>
        <button
          type="button"
          onClick={handleTogglePopover}
          className={selectStyles.selectTrigger}
          disabled={disabled}
          aria-expanded={isPopoverOpen}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault();
              handleTogglePopover();
            }
          }}
          aria-label={placeholder || "Select options"}
        >
          <MultiTriggerValue
            selectedValues={value || []}
            options={options}
            toggleOption={toggleOption}
            placeholder={placeholder}
            disabled={disabled}
          />
          <div className={selectStyles.selectButtonLayout}>
            {value.length > 0 && (
              <>
                <span
                  onClick={(event) => {
                    event.stopPropagation();
                    handleClear();
                  }}
                  className={selectStyles.closeButtonWrapper}
                >
                  <Icon name="close" />
                </span>
                <div className={selectStyles.separator}>
                  <Divider orientation="vertical" />
                </div>
              </>
            )}
            <Icon name={isPopoverOpen ? "chevron-up" : "chevron-down"} />
          </div>
        </button>
      </RadixPopover.Trigger>
      <RadixPopover.Portal>
        <RadixPopover.Content
          align="start"
          side={position}
          sideOffset={5}
          className={clsx(
            selectStyles.selectPopover,
            selectStyles.selectPortalRoot
          )}
          onEscapeKeyDown={() => setIsPopoverOpen(false)}
          role="dialog"
          aria-label="Select dropdown"
        >
          {/* CMDK filtering is OFF to support virtualization */}
          <Command shouldFilter={false}>
            {searchable && !loading && (
              <div className={selectStyles.selectSearchWrapper}>
                <Icon name="search" />
                <Command.Input
                  className={selectStyles.selectSearchInput}
                  placeholder={SEARCH_TEXT}
                  autoFocus
                  onValueChange={(v) => {
                    setSearch(v);
                  }}
                />
              </div>
            )}

            {showSelectHeaderActions && (
              <SelectHeaderActions
                value={value}
                filteredOptions={filteredOptions}
                onChange={onChange}
              />
            )}

            <Command.List
              ref={listRef}
              className={selectStyles.selectList}
              role="listbox"
              style={
                loading || filteredOptions.length === 0 ? viewPortStyle : {}
              }
            >
              {loading ? (
                <Command.Loading
                  className={selectStyles.selectListSecondaryState}
                >
                  {LOADING_TEXT}
                </Command.Loading>
              ) : filteredOptions.length === 0 ? (
                <div className={selectStyles.selectListSecondaryState}>
                  {NO_RESULTS_TEXT}
                </div>
              ) : (
                <Command.Group>
                  <div
                    style={{
                      height: `${virtualizer.getTotalSize()}px`,
                      width: "100%",
                      position: "relative"
                    }}
                  >
                    {virtualItems.map((vi) => {
                      const opt = filteredOptions[vi.index];
                      // Filter out non-relevant options in the first pass
                      if (!opt.relevant) return null;
                      const isSelected = value
                        ? value.includes(opt.value)
                        : false;

                      // Check if this is the last relevant option by looking at the actual filtered options array
                      const currentIndex = vi.index;
                      const nextOptionInList =
                        filteredOptions[currentIndex + 1];
                      const isLastRelevant =
                        opt.relevant &&
                        (!nextOptionInList || !nextOptionInList.relevant) &&
                        !allFilteredOptionsRelevant;

                      return (
                        <React.Fragment key={`relevant-${opt.value}`}>
                          <Command.Item
                            key={opt.value}
                            value={opt.value}
                            ref={virtualizer.measureElement}
                            data-index={vi.index}
                            className={clsx(
                              selectStyles.selectItem,
                              selectStyles.virtualizedItem
                            )}
                            role="option"
                            aria-selected={isSelected}
                            tabIndex={-1}
                            onSelect={() => toggleOption(opt.value)}
                            style={{
                              height: `${vi.size}px`,
                              transform: `translateY(${vi.start}px)`
                            }}
                          >
                            <Checkbox
                              variant="primary"
                              checked={isSelected}
                              onChange={() => toggleOption(opt.value)}
                            />
                            {opt.label}
                          </Command.Item>

                          {isLastRelevant && (
                            <div
                              key="divider"
                              style={{
                                position: "absolute",
                                top: `${vi.start + vi.size}px`,
                                left: 0,
                                right: 0,
                                zIndex: 1
                              }}
                            >
                              <Divider />
                            </div>
                          )}
                        </React.Fragment>
                      );
                    })}

                    {virtualItems.map((vi) => {
                      const opt = filteredOptions[vi.index];
                      if (opt.relevant) return null;
                      const isSelected = value
                        ? value.includes(opt.value)
                        : false;

                      return (
                        <React.Fragment key={`relevant-${opt.value}`}>
                          <Command.Item
                            key={opt.value}
                            value={opt.value}
                            ref={virtualizer.measureElement}
                            data-index={vi.index}
                            className={clsx(
                              selectStyles.selectItem,
                              selectStyles.virtualizedItem
                            )}
                            role="option"
                            aria-selected={isSelected}
                            tabIndex={-1}
                            onSelect={() => toggleOption(opt.value)}
                            style={{
                              height: `${vi.size}px`,
                              transform: `translateY(${vi.start}px)`
                            }}
                          >
                            <Checkbox
                              variant={"ghost"}
                              checked={isSelected}
                              onChange={() => toggleOption(opt.value)}
                            />
                            {opt.label}
                          </Command.Item>
                        </React.Fragment>
                      );
                    })}
                  </div>
                </Command.Group>
              )}
            </Command.List>
            {!loading && filteredOptions.length > 0 && (
              <SelectFooterActions
                clearDisabled={!value?.length}
                onClear={handleClear}
                onClose={() => setIsPopoverOpen(false)}
              />
            )}
          </Command>
        </RadixPopover.Content>
      </RadixPopover.Portal>
    </RadixPopover.Root>
  );
}
